import { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import { t, ehSegmentoClinica } from "../vocab";
import { useTheme } from "../components/Theme";

const STATUS_CORES = {
  ativo:    { color:"#00c875", label:"Ativo" },
  inativo:  { color:"var(--text-muted)", label:"Inativo" },
  pausado:  { color:"#fdab3d", label:"Pausado" },
  prospect: { color:"#579bfc", label:"Prospect" },
};

const SEGMENTOS = ["Indústria","Comércio","Serviços","Tecnologia","Saúde","Educação","Agronegócio","Construção","Logística","Outro"];
const COMO_CONHECEU = ["Instagram","Indicação","Google","Facebook","WhatsApp","Passou em frente","Já era paciente","Outro"];
const TIPOS_ATIV = ["nota","ligacao","reuniao","email","whatsapp","visita"];
const ICONE_ATIV = { nota:"📝", ligacao:"📞", reuniao:"🤝", email:"📧", whatsapp:"💬", visita:"🏢" };

function fmt(v) { return v ? `R$${Number(v).toLocaleString("pt-BR")}` : "—"; }
function fmtData(d) { if(!d) return "—"; const raw = new Date(d); const dt = new Date(raw.getTime() + raw.getTimezoneOffset()*60000); return dt.toLocaleDateString("pt-BR"); }

function StatusBadge({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const st = STATUS_CORES[status]||STATUS_CORES.ativo;
  useEffect(()=>{
    const h=e=>{ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown",h); return()=>document.removeEventListener("mousedown",h);
  },[]);
  return (
    <div ref={ref} style={{ position:"relative", display:"inline-flex" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ background:st.color, borderRadius:4, padding:"3px 12px", fontSize:12, fontWeight:600, color:"#fff", cursor:"pointer" }}>
        {st.label}
      </div>
      {open&&(
        <div style={{ position:"absolute", top:"110%", left:0, zIndex:500, background:"var(--card-bg)", border:"1px solid var(--border-color)", borderRadius:8, padding:6, minWidth:140, boxShadow:"0 8px 24px rgba(0,0,0,.12)" }}>
          {Object.entries(STATUS_CORES).map(([id,s])=>(
            <div key={id} onClick={()=>{ onChange(id); setOpen(false); }}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", borderRadius:6, cursor:"pointer", background:status===id?"var(--hover-bg)":"transparent" }}
              onMouseEnter={ev=>ev.currentTarget.style.background="var(--hover-bg)"}
              onMouseLeave={ev=>ev.currentTarget.style.background=status===id?"var(--hover-bg)":"transparent"}>
              <div style={{ width:10, height:10, borderRadius:2, background:s.color }}/>
              <span style={{ fontSize:13, color:"var(--text-primary)" }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IE({ value, onChange, placeholder="", style:s={} }) {
  const [edit, setEdit] = useState(false);
  const [val,  setVal]  = useState(value||"");
  const ref = useRef();
  useEffect(()=>setVal(value||""),[value]);
  useEffect(()=>{ if(edit&&ref.current) ref.current.focus(); },[edit]);
  const commit=()=>{ setEdit(false); if(String(val)!==String(value||"")) onChange(val); };
  if(edit) return (
    <input ref={ref} value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{ if(e.key==="Enter")commit(); if(e.key==="Escape"){setVal(value||"");setEdit(false);} }}
      style={{ background:"transparent", border:"none", borderBottom:"1.5px solid #EE6A1A", outline:"none", color:"var(--text-primary)", fontSize:13, width:"100%", padding:"1px 0", fontFamily:"'Inter',sans-serif", ...s }}/>
  );
  return (
    <div onClick={()=>setEdit(true)} style={{ cursor:"text", fontSize:13, color:value?"var(--text-primary)":"var(--text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", padding:"2px 4px", borderRadius:4, ...s }}
      onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {value||placeholder}
    </div>
  );
}

// Painel lateral de detalhes

// ---------------------------------------------------------------- Prontuário
// Registro clínico nasce travado: não se edita e não se apaga.
// Se faltou alguma coisa, a profissional escreve um adendo, datado e assinado.
function AbaProntuario({ cliente }) {
  const [dados, setDados]           = useState(null);
  const [erro, setErro]             = useState("");
  const [recado, setRecado]         = useState("");
  const [carregando, setCarregando] = useState(true);
  const [sub, setSub]               = useState("evolucao");
  const [salvando, setSalvando]     = useState(false);

  const [formEvo, setFormEvo]       = useState(null);
  const [formAnam, setFormAnam]     = useState(null);
  const [formTermo, setFormTermo]   = useState(null);
  const [adendoDe, setAdendoDe]     = useState(null);
  const [textoAdendo, setTextoAdendo] = useState("");

  const corpo = (r) => (r && r.data !== undefined ? r.data : r);
  const recadoDe = (e) => (e && e.response && e.response.data && e.response.data.erro) || "Não consegui completar.";
  const dt  = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");
  const dth = (v) => (v ? new Date(v).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

  const carregar = async () => {
    setCarregando(true); setErro("");
    try { setDados(corpo(await api.get(`/prontuario/paciente/${cliente._id}`))); }
    catch (e) { setErro(recadoDe(e)); }
    setCarregando(false);
  };

  useEffect(() => { if (cliente && cliente._id) carregar(); }, [cliente && cliente._id]); // eslint-disable-line

  const guardar = async (fn) => {
    if (salvando) return;
    setSalvando(true); setRecado("");
    try {
      await fn();
      setFormEvo(null); setFormAnam(null); setFormTermo(null); setAdendoDe(null); setTextoAdendo("");
      await carregar();
    } catch (e) { setRecado(recadoDe(e)); }
    setSalvando(false);
  };

  const caixa   = { background:"var(--hover-bg)", borderRadius:10, padding:"12px 14px", marginBottom:10 };
  const campo   = { width:"100%", padding:"8px 10px", borderRadius:8, border:"1px solid var(--border-color)", background:"var(--card-bg)", color:"var(--text-primary)", fontSize:13, fontFamily:"inherit", boxSizing:"border-box" };
  const botao   = { background:"#EE6A1A", border:"none", borderRadius:8, padding:"8px 14px", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:13 };
  const botao2  = { background:"transparent", border:"1px solid var(--border-color)", borderRadius:8, padding:"7px 12px", color:"var(--text-secondary)", cursor:"pointer", fontWeight:600, fontSize:12 };
  const rotulo  = { fontSize:11, color:"var(--text-secondary)", fontWeight:600, marginBottom:4 };

  if (carregando) return <div style={{ padding:"30px 0", textAlign:"center", color:"var(--text-muted)", fontSize:13 }}>Abrindo o prontuário...</div>;

  if (erro) return (
    <div style={{ background:"var(--hover-bg)", borderRadius:10, padding:"16px 18px", textAlign:"center" }}>
      <div style={{ fontSize:13, color:"var(--text-primary)", marginBottom:4 }}>{erro}</div>
      <div style={{ fontSize:12, color:"var(--text-muted)" }}>Prontuário é dado de saúde: o acesso é por perfil.</div>
    </div>
  );

  const d = dados || {};
  const anamnese  = d.anamnese;
  const perguntas = d.perguntas || [];
  const alertas   = (anamnese && anamnese.dados && anamnese.dados.__alertas) || [];

  const respostaTexto = (v) => (v === true ? "Sim" : v === false ? "Não" : (v || "—"));

  const Adendos = ({ lista }) => (
    (lista || []).map((a) => (
      <div key={a._id} style={{ borderLeft:"2px solid #EE6A1A", paddingLeft:10, marginTop:8 }}>
        <div style={{ fontSize:11, color:"var(--text-muted)" }}>Adendo · {dth(a.createdAt)} · {a.nomeCriador}</div>
        <div style={{ fontSize:13, color:"var(--text-primary)", lineHeight:1.5, whiteSpace:"pre-wrap" }}>{a.texto}</div>
      </div>
    ))
  );

  const BotaoAdendo = ({ registro }) => (
    adendoDe === registro._id ? (
      <div style={{ marginTop:10 }}>
        <textarea rows={3} value={textoAdendo} onChange={(e)=>setTextoAdendo(e.target.value)}
          placeholder="O que precisa ser acrescentado a este registro..." style={campo}/>
        <div style={{ display:"flex", gap:8, marginTop:8 }}>
          <button style={botao} disabled={salvando} onClick={()=>guardar(async()=>{
            await api.post(`/prontuario/${registro._id}/adendo`, { texto: textoAdendo });
          })}>{salvando ? "..." : "Salvar adendo"}</button>
          <button style={botao2} onClick={()=>{ setAdendoDe(null); setTextoAdendo(""); }}>Cancelar</button>
        </div>
      </div>
    ) : (
      <button style={{ ...botao2, marginTop:8 }} onClick={()=>{ setAdendoDe(registro._id); setTextoAdendo(""); }}>
        + Adendo
      </button>
    )
  );

  return (
    <div>
      {/* o que a profissional precisa ver antes de encostar na paciente */}
      {alertas.length > 0 && (
        <div style={{ background:"#FDF0E6", border:"1px solid #EE6A1A", borderRadius:10, padding:"12px 14px", marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"#B44A0A", marginBottom:6 }}>ATENÇÃO ANTES DE ATENDER</div>
          {alertas.map((a,i)=>(
            <div key={i} style={{ fontSize:13, color:"#7A3306", lineHeight:1.5 }}>• {a}</div>
          ))}
        </div>
      )}

      {!anamnese && (
        <div style={{ background:"var(--hover-bg)", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:13, color:"var(--text-primary)" }}>
          Esta paciente ainda não tem anamnese preenchida.
        </div>
      )}

      {recado && (
        <div style={{ background:"#FDE7DA", borderRadius:8, padding:"8px 12px", marginBottom:12, fontSize:12, color:"#B44A0A" }}>{recado}</div>
      )}

      {/* abas de dentro */}
      <div style={{ display:"flex", gap:6, marginBottom:16 }}>
        {[["evolucao", "Evolução", (d.evolucoes||[]).length],
          ["anamnese", "Anamnese", anamnese ? 1 : 0],
          ["termos",   "Termos",   (d.termos||[]).length]].map(([id,label,n])=>(
          <button key={id} onClick={()=>setSub(id)}
            style={{ padding:"6px 12px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer",
                     border:`1.5px solid ${sub===id?"#EE6A1A":"var(--border-color)"}`,
                     background:sub===id?"#FDE7DA":"var(--card-bg)",
                     color:sub===id?"#EE6A1A":"var(--text-secondary)" }}>
            {label}{n ? ` (${n})` : ""}
          </button>
        ))}
      </div>

      {/* ---------------------------------------------------------- EVOLUÇÃO */}
      {sub==="evolucao" && (
        <div>
          {!formEvo && (
            <button style={{ ...botao, marginBottom:14 }} onClick={()=>setFormEvo({
              texto:"", procedimento:"", dataAtendimento:new Date().toISOString().slice(0,10),
            })}>+ Registrar atendimento</button>
          )}

          {formEvo && (
            <div style={{ ...caixa, marginBottom:18 }}>
              <div style={{ display:"flex", gap:10, marginBottom:10 }}>
                <div style={{ flex:"0 0 140px" }}>
                  <div style={rotulo}>DATA</div>
                  <input type="date" style={campo} value={formEvo.dataAtendimento}
                    onChange={(e)=>setFormEvo({ ...formEvo, dataAtendimento:e.target.value })}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={rotulo}>PROCEDIMENTO</div>
                  <input style={campo} value={formEvo.procedimento} placeholder="Ex.: Limpeza de pele profunda"
                    onChange={(e)=>setFormEvo({ ...formEvo, procedimento:e.target.value })}/>
                </div>
              </div>
              <div style={rotulo}>O QUE FOI FEITO E COMO A PACIENTE REAGIU</div>
              <textarea rows={6} style={campo} value={formEvo.texto}
                placeholder="Produtos e parâmetros usados, região tratada, intercorrências, orientações dadas..."
                onChange={(e)=>setFormEvo({ ...formEvo, texto:e.target.value })}/>
              <div style={{ fontSize:11, color:"var(--text-muted)", margin:"8px 0" }}>
                Depois de salvo este registro não pode mais ser alterado. Correções entram como adendo.
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={botao} disabled={salvando} onClick={()=>guardar(async()=>{
                  await api.post("/prontuario/evolucao", {
                    pacienteId: cliente._id, pacienteNome: cliente.nomeEmpresa || cliente.nomeContato || "",
                    texto: formEvo.texto, procedimento: formEvo.procedimento,
                    dataAtendimento: new Date(formEvo.dataAtendimento + "T12:00:00").toISOString(),
                  });
                })}>{salvando ? "Salvando..." : "Salvar registro"}</button>
                <button style={botao2} onClick={()=>setFormEvo(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {(d.evolucoes||[]).length===0 && !formEvo && (
            <div style={{ textAlign:"center", padding:"30px 0", color:"var(--text-muted)", fontSize:13 }}>Nenhum atendimento registrado</div>
          )}

          {(d.evolucoes||[]).map((e)=>(
            <div key={e._id} style={caixa}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6, gap:10 }}>
                <span style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)" }}>{e.procedimento || "Atendimento"}</span>
                <span style={{ fontSize:11, color:"var(--text-muted)", whiteSpace:"nowrap" }}>{dt(e.dataAtendimento || e.createdAt)}</span>
              </div>
              <div style={{ fontSize:13, color:"var(--text-primary)", lineHeight:1.6, whiteSpace:"pre-wrap" }}>{e.texto}</div>
              <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:8 }}>
                Registrado por {e.nomeCriador || e.profissionalNome || "—"} em {dth(e.createdAt)}
              </div>
              <Adendos lista={e.adendos}/>
              <BotaoAdendo registro={e}/>
            </div>
          ))}
        </div>
      )}

      {/* ---------------------------------------------------------- ANAMNESE */}
      {sub==="anamnese" && (
        <div>
          {!formAnam && (
            <button style={{ ...botao, marginBottom:14 }} onClick={()=>{
              const base = {};
              perguntas.forEach((p)=>{ base[p.id] = (anamnese && anamnese.dados ? anamnese.dados[p.id] : "") || ""; });
              setFormAnam(base);
            }}>{anamnese ? "Atualizar anamnese" : "Preencher anamnese"}</button>
          )}

          {formAnam && (
            <div style={{ ...caixa, marginBottom:18 }}>
              {perguntas.map((p)=>(
                <div key={p.id} style={{ marginBottom:12 }}>
                  <div style={{ ...rotulo, color:p.alerta?"#B44A0A":"var(--text-secondary)" }}>
                    {p.label}{p.alerta ? " *" : ""}
                  </div>
                  {p.tipo==="sim_nao" ? (
                    <div style={{ display:"flex", gap:6 }}>
                      {["Sim","Não"].map((op)=>(
                        <button key={op} onClick={()=>setFormAnam({ ...formAnam, [p.id]: op })}
                          style={{ padding:"5px 14px", borderRadius:7, fontSize:12, fontWeight:700, cursor:"pointer",
                                   border:`1.5px solid ${formAnam[p.id]===op?"#EE6A1A":"var(--border-color)"}`,
                                   background:formAnam[p.id]===op?"#FDE7DA":"var(--card-bg)",
                                   color:formAnam[p.id]===op?"#EE6A1A":"var(--text-secondary)" }}>{op}</button>
                      ))}
                    </div>
                  ) : (
                    <input style={campo} value={formAnam[p.id]||""} placeholder="Se não, deixe em branco"
                      onChange={(ev)=>setFormAnam({ ...formAnam, [p.id]: ev.target.value })}/>
                  )}
                </div>
              ))}
              <div style={{ fontSize:11, color:"var(--text-muted)", margin:"8px 0" }}>
                As respostas com * são as que geram aviso para a profissional antes do atendimento.
                A versão anterior fica guardada.
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={botao} disabled={salvando} onClick={()=>guardar(async()=>{
                  await api.post("/prontuario/anamnese", {
                    pacienteId: cliente._id, pacienteNome: cliente.nomeEmpresa || cliente.nomeContato || "",
                    respostas: formAnam,
                  });
                })}>{salvando ? "Salvando..." : "Salvar anamnese"}</button>
                <button style={botao2} onClick={()=>setFormAnam(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {anamnese && !formAnam && (
            <div style={caixa}>
              <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:10 }}>
                Preenchida em {dth(anamnese.createdAt)} por {anamnese.nomeCriador || "—"}
              </div>
              {perguntas.map((p)=>(
                <div key={p.id} style={{ display:"flex", gap:10, padding:"5px 0", borderBottom:"1px solid var(--border-color)" }}>
                  <span style={{ flex:1, fontSize:12, color:"var(--text-secondary)" }}>{p.label}</span>
                  <span style={{ flex:"0 0 40%", fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>
                    {respostaTexto(anamnese.dados ? anamnese.dados[p.id] : "")}
                  </span>
                </div>
              ))}
              <Adendos lista={anamnese.adendos}/>
              <BotaoAdendo registro={anamnese}/>
            </div>
          )}

          {(d.anamnesesAntigas||[]).length>0 && (
            <div style={{ marginTop:14, fontSize:11, color:"var(--text-muted)" }}>
              {(d.anamnesesAntigas||[]).length} versão(ões) anterior(es) guardada(s) — nenhuma foi apagada.
            </div>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------- TERMOS */}
      {sub==="termos" && (
        <div>
          {!formTermo && (
            <button style={{ ...botao, marginBottom:14 }} onClick={()=>setFormTermo({
              titulo:"Termo de consentimento para procedimento estético", procedimento:"", texto:"", aceito:false,
            })}>+ Registrar termo assinado</button>
          )}

          {formTermo && (
            <div style={{ ...caixa, marginBottom:18 }}>
              <div style={rotulo}>TÍTULO</div>
              <input style={campo} value={formTermo.titulo}
                onChange={(e)=>setFormTermo({ ...formTermo, titulo:e.target.value })}/>
              <div style={{ ...rotulo, marginTop:10 }}>PROCEDIMENTO</div>
              <input style={campo} value={formTermo.procedimento} placeholder="A que procedimento este termo se refere"
                onChange={(e)=>setFormTermo({ ...formTermo, procedimento:e.target.value })}/>
              <div style={{ ...rotulo, marginTop:10 }}>TEXTO DO TERMO QUE A PACIENTE LEU E ACEITOU</div>
              <textarea rows={7} style={campo} value={formTermo.texto}
                onChange={(e)=>setFormTermo({ ...formTermo, texto:e.target.value })}/>
              <label style={{ display:"flex", gap:8, alignItems:"center", margin:"10px 0", fontSize:12, color:"var(--text-primary)", cursor:"pointer" }}>
                <input type="checkbox" checked={formTermo.aceito}
                  onChange={(e)=>setFormTermo({ ...formTermo, aceito:e.target.checked })}/>
                Confirmo que a paciente leu e aceitou este termo
              </label>
              <div style={{ display:"flex", gap:8 }}>
                <button style={{ ...botao, opacity: formTermo.aceito ? 1 : .5 }} disabled={salvando || !formTermo.aceito}
                  onClick={()=>guardar(async()=>{
                    await api.post("/prontuario/termo", {
                      pacienteId: cliente._id, pacienteNome: cliente.nomeEmpresa || cliente.nomeContato || "",
                      titulo: formTermo.titulo, procedimento: formTermo.procedimento,
                      texto: formTermo.texto, aceitoPor: cliente.nomeEmpresa || "", origem: "balcao",
                    });
                  })}>{salvando ? "Salvando..." : "Guardar termo"}</button>
                <button style={botao2} onClick={()=>setFormTermo(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {(d.termos||[]).length===0 && !formTermo && (
            <div style={{ textAlign:"center", padding:"30px 0", color:"var(--text-muted)", fontSize:13 }}>Nenhum termo guardado</div>
          )}

          {(d.termos||[]).map((x)=>(
            <div key={x._id} style={caixa}>
              <div style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)", marginBottom:4 }}>
                {(x.dados && x.dados.titulo) || "Termo"}
              </div>
              <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:8 }}>
                Aceito em {dth(x.dados && x.dados.aceitoEm)}
                {x.dados && x.dados.ip ? ` · IP ${x.dados.ip}` : ""}
                {x.procedimento ? ` · ${x.procedimento}` : ""}
              </div>
              <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.6, whiteSpace:"pre-wrap", maxHeight:160, overflow:"auto" }}>{x.texto}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PainelDetalhe({ cliente, onClose, onAtualizar, usuarios , abaInicial}) {
  const [nota, setNota]   = useState("");
  const [tipoNota, setTipoNota] = useState("nota");
  const [load, setLoad]   = useState(false);
  const [aba, setAba] = useState(abaInicial || "info");

  const addAtividade = async () => {
    if (!nota.trim()) return;
    setLoad(true);
    try {
      await api.post(`/clientes/${cliente._id}/atividades`, { tipo:tipoNota, descricao:nota });
      setNota(""); onAtualizar();
    } finally { setLoad(false); }
  };

  const inp = { width:"100%", boxSizing:"border-box", background:"var(--hover-bg)", border:"1.5px solid var(--border-color)", borderRadius:8, padding:"8px 10px", color:"var(--text-primary)", fontSize:13, outline:"none", fontFamily:"'Inter',sans-serif" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.3)", zIndex:1000, display:"flex", justifyContent:"flex-end" }} onClick={onClose}>
      <div style={{ width:"100%", maxWidth:"min(1040px, 72vw)", maxWidth:"96vw", height:"100vh", background:"var(--card-bg)", boxShadow:"-8px 0 32px rgba(0,0,0,.12)", overflowY:"auto", display:"flex", flexDirection:"column" }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid var(--border-color)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
            <div>
              <div style={{ fontSize:11, color:"#EE6A1A", fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>CLIENTE</div>
              <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:"var(--text-primary)", letterSpacing:"-.02em" }}>{cliente.nomeEmpresa}</h2>
              {cliente.nomeContato&&<div style={{ fontSize:13, color:"var(--text-secondary)", marginTop:3 }}>{cliente.nomeContato}</div>}
            </div>
            <button onClick={onClose} style={{ background:"var(--hover-bg)", border:"none", borderRadius:8, width:32, height:32, color:"var(--text-secondary)", fontSize:18, cursor:"pointer" }}>×</button>
          </div>
          <StatusBadge status={cliente.status} onChange={async(s)=>{ await api.put(`/clientes/${cliente._id}`,{status:s}); onAtualizar(); }}/>
        </div>

        {/* Abas */}
        <div style={{ display:"flex", borderBottom:"1px solid var(--border-color)", padding:"0 24px" }}>
          {(ehSegmentoClinica()
            ?[["info","📋 Info"],["prontuario","🩺 Prontuário"],["historico","📝 Histórico"]]
            :[["info","📋 Info"],["contratos","💰 Contratos"],["historico","📝 Histórico"]]
          ).map(([id,label])=>(
            <button key={id} onClick={()=>setAba(id)} style={{ padding:"10px 14px", fontSize:13, fontWeight:aba===id?600:400, cursor:"pointer", border:"none", background:"transparent", color:aba===id?"#EE6A1A":"var(--text-secondary)", borderBottom:aba===id?"2px solid #EE6A1A":"2px solid transparent", marginBottom:-1 }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ flex:1, padding:"20px 24px" }}>

          {/* ABA INFO */}
          {aba==="info"&&(
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
                {[
                  ["🏢 Empresa",    cliente.nomeEmpresa,  "nomeEmpresa"],
                  ["👤 Contato",    cliente.nomeContato,  "nomeContato"],
                  ["📋 CNPJ",       cliente.cnpj,         "cnpj"],
                  ["🏭 Segmento",   cliente.segmento,     "segmento"],
                  ["📞 Telefone",   cliente.telefone,     "telefone"],
                  ["📧 Email",      cliente.email,        "email"],
                  ["🌐 Site",       cliente.site,         "site"],
                  ["📍 Cidade/UF",  `${cliente.cidade||""}${cliente.estado?"/"+cliente.estado:""}`, null],
                  ["👥 Responsável",cliente.nomeResponsavel,"nomeResponsavel"],
                  ["📅 Desde",      fmtData(cliente.dataInicio), null],
                ].map(([label,val,campo])=>(
                  <div key={label}>
                    <div style={{ fontSize:11, color:"var(--text-secondary)", fontWeight:600, marginBottom:4, letterSpacing:".02em" }}>{label}</div>
                    <div style={{ fontSize:13, color:val?"var(--text-primary)":"var(--text-muted)", fontWeight:val?500:400 }}>{val||"—"}</div>
                  </div>
                ))}
              </div>

              {cliente.observacoes&&(
                <div style={{ background:"var(--hover-bg)", borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontSize:11, color:"var(--text-secondary)", fontWeight:600, marginBottom:6 }}>OBSERVAÇÕES</div>
                  <div style={{ fontSize:13, color:"var(--text-primary)", lineHeight:1.6 }}>{cliente.observacoes}</div>
                </div>
              )}
            </div>
          )}

          {/* ABA CONTRATOS */}
          {aba==="contratos"&&!ehSegmentoClinica()&&(
            <div>
              {/* Resumo */}
              <div style={{ display:"flex", gap:12, marginBottom:20 }}>
                <div style={{ flex:1, background:"var(--hover-bg)", borderRadius:10, padding:"12px 14px", textAlign:"center" }}>
                  <div style={{ fontSize:11, color:"var(--text-secondary)", marginBottom:4 }}>MRR</div>
                  <div style={{ fontSize:22, fontWeight:800, color:"#EE6A1A" }}>{fmt(cliente.valorMensal)}</div>
                </div>
                <div style={{ flex:1, background:"var(--hover-bg)", borderRadius:10, padding:"12px 14px", textAlign:"center" }}>
                  <div style={{ fontSize:11, color:"var(--text-secondary)", marginBottom:4 }}>Contratos ativos</div>
                  <div style={{ fontSize:22, fontWeight:800, color:"#00c875" }}>{(cliente.contratos||[]).filter(c=>c.status==="ativo").length}</div>
                </div>
              </div>

              {/* Lista de contratos */}
              {(cliente.contratos||[]).length===0&&(
                <div style={{ textAlign:"center", padding:"30px 0", color:"var(--text-muted)", fontSize:13 }}>Nenhum contrato cadastrado</div>
              )}
              {(cliente.contratos||[]).map((c,i)=>(
                <div key={i} style={{ background:"var(--hover-bg)", borderRadius:10, padding:"12px 14px", marginBottom:10, borderLeft:`3px solid ${c.status==="ativo"?"#00c875":c.status==="pausado"?"#fdab3d":"var(--text-muted)"}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)" }}>{c.descricao||"Contrato"}</span>
                    <span style={{ fontSize:13, fontWeight:800, color:"#EE6A1A" }}>{fmt(c.valorMensal)}/mês</span>
                  </div>
                  <div style={{ fontSize:12, color:"var(--text-secondary)" }}>
                    Início: {fmtData(c.dataInicio)} · Status: <span style={{ color:c.status==="ativo"?"#00c875":"#fdab3d", fontWeight:600 }}>{c.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ABA PRONTUÁRIO */}
          {aba==="prontuario"&&ehSegmentoClinica()&&(
            <AbaProntuario cliente={cliente}/>
          )}

          {/* ABA HISTÓRICO */}
          {aba==="historico"&&(
            <div>
              {/* Adicionar atividade */}
              <div style={{ background:"var(--hover-bg)", borderRadius:12, padding:14, marginBottom:20 }}>
                <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
                  {TIPOS_ATIV.map(t=>(
                    <button key={t} onClick={()=>setTipoNota(t)}
                      style={{ padding:"4px 10px", borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer", border:`1.5px solid ${tipoNota===t?"#EE6A1A":"var(--border-color)"}`, background:tipoNota===t?"#FDE7DA":"var(--card-bg)", color:tipoNota===t?"#EE6A1A":"var(--text-secondary)" }}>
                      {ICONE_ATIV[t]} {t}
                    </button>
                  ))}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <input value={nota} onChange={e=>setNota(e.target.value)} placeholder="Adicionar atividade..."
                    onKeyDown={e=>e.key==="Enter"&&addAtividade()}
                    style={{ ...inp, flex:1 }}
                    onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}/>
                  <button onClick={addAtividade} disabled={load||!nota.trim()}
                    style={{ background:"#EE6A1A", border:"none", borderRadius:8, padding:"8px 14px", color:"#fff", cursor:"pointer", fontWeight:700, opacity:!nota.trim()?.5:1 }}>
                    {load?"...":"+"}
                  </button>
                </div>
              </div>

              {/* Lista de atividades */}
              {(cliente.atividades||[]).length===0&&(
                <div style={{ textAlign:"center", padding:"30px 0", color:"var(--text-muted)", fontSize:13 }}>Nenhuma atividade registrada</div>
              )}
              {[...(cliente.atividades||[])].reverse().map((a,i)=>(
                <div key={i} style={{ display:"flex", gap:12, marginBottom:14, alignItems:"flex-start" }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:"var(--hover-bg)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                    {ICONE_ATIV[a.tipo]||"📝"}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:"#EE6A1A" }}>{a.nomeUsuario}</span>
                      <span style={{ fontSize:11, color:"var(--text-muted)" }}>{new Date(a.createdAt).toLocaleDateString("pt-BR")}</span>
                    </div>
                    <div style={{ fontSize:13, color:"var(--text-primary)", lineHeight:1.5 }}>{a.descricao}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal cadastrar/editar cliente
function ModalCliente({ cliente, onClose, onSave, onDelete, usuarios }) {
  const isNew = !cliente?._id;
  const [form, setForm] = useState({
    nomeEmpresa: cliente?.nomeEmpresa||"", nomeContato: cliente?.nomeContato||"",
    cnpj: cliente?.cnpj||"", segmento: cliente?.segmento||"",
    telefone: cliente?.telefone||"", email: cliente?.email||"",
    site: cliente?.site||"", cidade: cliente?.cidade||"",
    dataNascimento: cliente?.dataNascimento?new Date(cliente.dataNascimento).toISOString().slice(0,10):"",
    comoConheceu: cliente?.comoConheceu||"",
    estado: cliente?.estado||"", endereco: cliente?.endereco||"",
    valorMensal: cliente?.valorMensal||"",
    dataInicio: cliente?.dataInicio?new Date(cliente.dataInicio).toISOString().slice(0,10):"",
    dataRenovacao: cliente?.dataRenovacao?new Date(cliente.dataRenovacao).toISOString().slice(0,10):"",
    nomeResponsavel: cliente?.nomeResponsavel||"",
    status: cliente?.status||"ativo",
    observacoes: cliente?.observacoes||"",
  });
  const [load, setLoad] = useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const inp={ width:"100%", boxSizing:"border-box", background:"var(--hover-bg)", border:"1.5px solid var(--border-color)", borderRadius:8, padding:"9px 12px", color:"var(--text-primary)", fontSize:13, outline:"none", fontFamily:"'Inter',sans-serif" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:"var(--card-bg)", borderRadius:16, padding:28, width:"100%", maxWidth:600, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,.2)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
          <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:"var(--text-primary)" }}>{isNew?t("Novo cliente"):t("Editar cliente")}</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-secondary)", fontSize:20 }}>×</button>
        </div>

        {/* Status */}
        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:8, letterSpacing:".04em", textTransform:"uppercase" }}>Status</label>
          <div style={{ display:"flex", gap:8 }}>
            {Object.entries(STATUS_CORES).map(([id,s])=>(
              <button key={id} onClick={()=>set("status",id)}
                style={{ padding:"5px 14px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer", border:`1.5px solid ${form.status===id?s.color:"var(--border-color)"}`, background:form.status===id?s.color+"18":"var(--card-bg)", color:form.status===id?s.color:"var(--text-secondary)" }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          {(ehSegmentoClinica()?[
            ["Nome do paciente *","nomeEmpresa"],["CPF","cnpj"],
            ["Telefone","telefone"],["Email","email"],
            ["Data de nascimento","dataNascimento"],["Como conheceu","comoConheceu"],
            ["Cidade","cidade"],["Estado","estado"],
            ["Endereço","endereco"],
          ]:[
            ["Nome da empresa *","nomeEmpresa"],["Nome do contato","nomeContato"],
            ["CNPJ","cnpj"],["Segmento","segmento"],
            ["Telefone","telefone"],["Email","email"],
            ["Site","site"],["Cidade","cidade"],
            ["Estado","estado"],["Endereço","endereco"],
          ]).map(([label,key])=>(
            <div key={key} style={key==="endereco"||key==="nomeEmpresa"?{gridColumn:"span 2"}:{}}>
              <label style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:5, letterSpacing:".04em", textTransform:"uppercase" }}>{label}</label>
              {(key==="segmento"||key==="comoConheceu")?(
                <select value={form[key]||""} onChange={e=>set(key,e.target.value)} style={{ ...inp, appearance:"auto" }}
                  onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}>
                  <option value="">Selecionar...</option>
                  {(key==="comoConheceu"?COMO_CONHECEU:SEGMENTOS).map(s=><option key={s}>{s}</option>)}
                </select>
              ):(
                <input type={key==="dataNascimento"?"date":"text"} value={form[key]||""} onChange={e=>set(key,e.target.value)} style={inp}
                  onFocus={e=>{ e.target.style.borderColor="#EE6A1A"; e.target.style.boxShadow="0 0 0 3px #EE6A1A18"; }}
                  onBlur={e=>{ e.target.style.borderColor="var(--border-color)"; e.target.style.boxShadow="none"; }}/>
              )}
            </div>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns: ehSegmentoClinica()?"1fr":"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
          {!ehSegmentoClinica()&&<div>
            <label style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:5, letterSpacing:".04em", textTransform:"uppercase" }}>Valor mensal (R$)</label>
            <input type="number" value={form.valorMensal||""} onChange={e=>set("valorMensal",e.target.value)} style={inp}
              onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}/>
          </div>}
          <div>
            <label style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:5, letterSpacing:".04em", textTransform:"uppercase" }}>{t("Cliente desde")}</label>
            <input type="date" value={form.dataInicio||""} onChange={e=>set("dataInicio",e.target.value)} style={inp}
              onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}/>
          </div>
          {!ehSegmentoClinica()&&<div>
            <label style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:5, letterSpacing:".04em", textTransform:"uppercase" }}>Renovação</label>
            <input type="date" value={form.dataRenovacao||""} onChange={e=>set("dataRenovacao",e.target.value)} style={inp}
              onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}/>
          </div>}
        </div>

        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:5, letterSpacing:".04em", textTransform:"uppercase" }}>Responsável</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {(usuarios||[]).map(u=>(
              <button key={u._id} onClick={()=>set("nomeResponsavel",u.nome)}
                style={{ padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer", border:`1.5px solid ${form.nomeResponsavel===u.nome?"#EE6A1A":"var(--border-color)"}`, background:form.nomeResponsavel===u.nome?"#FDE7DA":"var(--card-bg)", color:form.nomeResponsavel===u.nome?"#EE6A1A":"var(--text-secondary)" }}>
                {u.nome.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:5, letterSpacing:".04em", textTransform:"uppercase" }}>Observações</label>
          <textarea value={form.observacoes||""} onChange={e=>set("observacoes",e.target.value)} rows={3} style={{ ...inp, resize:"vertical" }}
            onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}
            placeholder={t("Informações relevantes sobre o cliente...")}/>
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={async()=>{ if(!form.nomeEmpresa.trim()) return alert(t("Nome da empresa obrigatório")); setLoad(true); try{await onSave({...cliente,...form});}finally{setLoad(false);} }} disabled={load}
            style={{ flex:1, background:"#EE6A1A", border:"none", borderRadius:10, padding:"13px 0", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer", opacity:load?.7:1 }}>
            {load?"Salvando...":t("Salvar cliente")}
          </button>
          {!isNew&&<button onClick={()=>onDelete(cliente._id)} style={{ background:"#feecec", border:"1px solid #f9d3d3", borderRadius:10, padding:"13px 16px", color:"#e2445c", fontWeight:600, fontSize:13, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ─────────────────────────────────
export default function Clientes({ currentUser }) {
  const { theme } = useTheme();
  const [clientes,  setClientes]  = useState([]);
  const [usuarios,  setUsuarios]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [modal,     setModal]     = useState(null);
  const [detalhe,   setDetalhe]   = useState(null);
  const [abaInicial, setAbaInicial] = useState("info");
  const [novaLinha, setNovaLinha] = useState(false);

  const carregar = useCallback(async()=>{
    try{
      const [cr, ur] = await Promise.all([
        api.get("/clientes?limit=1000"),
        api.get("/users"),
      ]);
      setClientes(cr.data.clientes);
      setUsuarios(ur.data);
    }catch{}finally{ setLoading(false); }
  },[]);

  useEffect(()=>{ carregar(); },[carregar]);

  const salvar = async(form)=>{
    if(form._id){ const{data}=await api.put(`/clientes/${form._id}`,form); setClientes(prev=>prev.map(c=>c._id===data._id?data:c)); }
    else{ const{data}=await api.post("/clientes",form); setClientes(prev=>[data,...prev]); }
    setModal(null); carregar();
  };
  const excluir = async(id)=>{ if (!window.confirm("Excluir este cliente?")) return; await api.delete(`/clientes/${id}`); setClientes(prev=>prev.filter(c=>c._id!==id)); setModal(null); setDetalhe(null); };
  const atualizarCampo = async(id,k,v)=>{
    const{data}=await api.put(`/clientes/${id}`,{[k]:v});
    setClientes(prev=>prev.map(c=>c._id===id?{...c,...data}:c));
  };
  const adicionarInline = async(nome)=>{
    const{data}=await api.post("/clientes",{nomeEmpresa:nome,status:"ativo",nomeResponsavel:currentUser?.nome});
    setClientes(prev=>[data,...prev]); setNovaLinha(false);
  };

  const filtered = clientes.filter(c=>{
    const q=search.toLowerCase();
    const matchSearch=!q||c.nomeEmpresa?.toLowerCase().includes(q)||c.nomeContato?.toLowerCase().includes(q)||c.cnpj?.includes(q);
    const matchStatus=filtroStatus==="todos"||c.status===filtroStatus;
    return matchSearch&&matchStatus;
  });

  const mrr = clientes.filter(c=>c.status==="ativo").reduce((a,c)=>a+Number(c.valorMensal||0),0);

  const TH=({w,children,center})=>(
    <th style={{ padding:"8px 12px", fontSize:11, fontWeight:600, color:"var(--text-secondary)", letterSpacing:".04em", textTransform:"uppercase", textAlign:center?"center":"left", borderRight:"1px solid var(--border-color)", width:w, minWidth:w, background:"var(--hover-bg)", whiteSpace:"nowrap" }}>{children}</th>
  );
  const TD=({w,children,center,style:s={}})=>(
    <td style={{ padding:"0 10px", height:46, borderRight:"1px solid var(--border-color)", borderBottom:"1px solid var(--border-light)", width:w, minWidth:w, textAlign:center?"center":"left", verticalAlign:"middle", ...s }}>{children}</td>
  );

  return (
    <div style={{ fontFamily:"'Inter',-apple-system,sans-serif", background:"var(--hover-bg)", minHeight:"100vh" }}>

      {/* Header */}
      <div style={{ background:"var(--card-bg)", borderBottom:"1px solid var(--border-color)", padding:"16px 24px 0" }}>

        {/* Métricas */}
        <div style={{ display:"flex", gap:10, marginBottom:14, overflowX:"auto" }}>
          {[
            ["Total",    clientes.length,                                          "var(--text-primary)"],
            ["Ativos",   clientes.filter(c=>c.status==="ativo").length,            "#00c875"],
            ["Inativos", clientes.filter(c=>c.status==="inativo").length,          "var(--text-muted)"],
            // receita recorrente e metrica de software por assinatura, nao de clinica
            ...(ehSegmentoClinica() ? [] : [["MRR",      `R$${mrr.toLocaleString("pt-BR")}`,                       "#EE6A1A"]]),
          ].map(([label,val,color])=>(
            <div key={label} style={{ flex:"0 0 auto", background:"var(--card-bg)", border:"1px solid var(--border-color)", borderRadius:8, padding:"10px 16px", minWidth:110, borderTop:`3px solid ${color}` }}>
              <div style={{ fontSize:10, color:"var(--text-secondary)", fontWeight:700, textTransform:"uppercase", letterSpacing:".05em", marginBottom:4 }}>{label}</div>
              <div style={{ fontSize:22, fontWeight:800, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display:"flex", gap:10, paddingBottom:12, alignItems:"center", flexWrap:"wrap" }}>
          <input placeholder={t("🔍 Buscar cliente, CNPJ...")} value={search} onChange={e=>setSearch(e.target.value)}
            style={{ background:"var(--hover-bg)", border:"1.5px solid var(--border-color)", borderRadius:8, padding:"7px 12px", color:"var(--text-primary)", fontSize:13, outline:"none", width:250, fontFamily:"'Inter',sans-serif" }}
            onFocus={e=>{ e.target.style.borderColor="#EE6A1A"; e.target.style.background="var(--card-bg)"; }}
            onBlur={e=>{ e.target.style.borderColor="var(--border-color)"; e.target.style.background="var(--hover-bg)"; }}/>

          {/* Filtro status */}
          <div style={{ display:"flex", background:"var(--hover-bg)", borderRadius:8, padding:3, gap:2 }}>
            {[["todos","Todos"],...Object.entries(STATUS_CORES).map(([id,s])=>[id,s.label])].map(([id,label])=>(
              <button key={id} onClick={()=>setFiltroStatus(id)} style={{ padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:filtroStatus===id?700:400, cursor:"pointer", background:filtroStatus===id?"var(--card-bg)":"transparent", border:filtroStatus===id?"1px solid var(--border-color)":"none", color:filtroStatus===id?"var(--text-primary)":"var(--text-secondary)" }}>
                {label}
              </button>
            ))}
          </div>

          <span style={{ fontSize:13, color:"var(--text-secondary)" }}>{filtered.length} {t(filtered.length!==1?"clientes":"cliente")}</span>

          <button onClick={()=>setModal({})} style={{ marginLeft:"auto", background:"#EE6A1A", border:"none", borderRadius:8, padding:"7px 18px", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", boxShadow:"0 2px 8px #EE6A1A44" }}
            onMouseEnter={e=>e.currentTarget.style.background="#0060c0"}
            onMouseLeave={e=>e.currentTarget.style.background="#EE6A1A"}>
            {t("+ Novo cliente")}
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ overflowX:"auto", padding:"16px 0 48px" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:980 }}>
          <thead>
            <tr>
              <TH w={280}>{t("Empresa")}</TH>
              {!ehSegmentoClinica()&&<TH w={160}>{t("Contato")}</TH>}
              <TH w={130}>Telefone</TH>
              <TH w={200}>Email</TH>
              <TH w={120}>{t("Segmento")}</TH>
              <TH w={120} center>Status</TH>
              {!ehSegmentoClinica()&&<TH w={120}>MRR</TH>}
              <TH w={110}>{t("Cliente desde")}</TH>
              <TH w={120}>Responsável</TH>
              <TH w={44} center/>
            </tr>
          </thead>
          <tbody>
            {loading&&<tr><td colSpan={ehSegmentoClinica()?9:10} style={{ padding:60, textAlign:"center", color:"var(--text-secondary)" }}>Carregando...</td></tr>}

            {/* Nova linha inline */}
            {!loading&&novaLinha&&(
              <tr style={{ background:"var(--hover-bg)" }}>
                <td colSpan={2} style={{ padding:"8px 14px", borderBottom:"1px solid var(--border-light)" }}>
                  <input autoFocus placeholder={t("Nome da empresa... (Enter para salvar)")}
                    onKeyDown={e=>{ if(e.key==="Enter"&&e.target.value.trim()) adicionarInline(e.target.value.trim()); if(e.key==="Escape") setNovaLinha(false); }}
                    style={{ background:"transparent", border:"none", borderBottom:"1.5px solid #EE6A1A", outline:"none", color:"var(--text-primary)", fontSize:13, width:"100%", padding:"2px 0", fontFamily:"'Inter',sans-serif", fontWeight:600 }}/>
                </td>
                <td colSpan={ehSegmentoClinica()?7:8} style={{ padding:"8px 12px", borderBottom:"1px solid var(--border-light)" }}>
                  <span style={{ fontSize:11, color:"var(--text-muted)" }}>↵ Enter · Esc cancelar</span>
                </td>
              </tr>
            )}

            {!loading&&filtered.map(cliente=>{
              const st = STATUS_CORES[cliente.status]||STATUS_CORES.ativo;
              const waLink = cliente.telefone?`https://wa.me/55${cliente.telefone.replace(/\D/g,"")}`:null;
              return (
                <tr key={cliente._id} style={{ background:"var(--card-bg)", cursor:"pointer" }}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"}
                  onMouseLeave={e=>e.currentTarget.style.background="var(--card-bg)"}>

                  <TD w={280}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:32, height:32, borderRadius:8, background:st.color+"22", border:`1.5px solid ${st.color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0, fontWeight:700, color:st.color }}>
                        {cliente.nomeEmpresa?.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex:1, overflow:"hidden" }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)" }} onClick={()=>{ setAbaInicial("info"); setDetalhe(cliente); }}>{cliente.nomeEmpresa}</div>
                        {cliente.cnpj&&<div style={{ fontSize:11, color:"var(--text-muted)" }}>{cliente.cnpj}</div>}
                      </div>
                      <button onClick={()=>setModal(cliente)} style={{ background:"none", border:"none", color:"var(--text-muted)", cursor:"pointer", fontSize:12, opacity:0, flexShrink:0 }}
                        onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}>✏️</button>
                    </div>
                  </TD>
                  {!ehSegmentoClinica()&&(
                    <TD w={160}><IE value={cliente.nomeContato} onChange={v=>atualizarCampo(cliente._id,"nomeContato",v)} placeholder="—"/></TD>
                  )}
                  <TD w={130}>
                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                      <IE value={cliente.telefone} onChange={v=>atualizarCampo(cliente._id,"telefone",v)} placeholder="—" style={{ fontSize:12 }}/>
                      {waLink&&<a href={waLink} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ color:"#25d366", fontSize:13, textDecoration:"none" }}>💬</a>}
                    </div>
                  </TD>
                  <TD w={200}><IE value={cliente.email} onChange={v=>atualizarCampo(cliente._id,"email",v)} placeholder="—" style={{ color:"#EE6A1A", fontSize:12 }}/></TD>
                  <TD w={120}>
                    {cliente.segmento
                      ? <span style={{ background:"var(--hover-bg)", borderRadius:4, padding:"2px 8px", fontSize:12, color:"var(--text-secondary)" }}>{cliente.segmento}</span>
                      : <span style={{ color:"var(--text-muted)", fontSize:12 }}>—</span>
                    }
                  </TD>
                  <TD w={120} center>
                    <StatusBadge status={cliente.status} onChange={v=>atualizarCampo(cliente._id,"status",v)}/>
                  </TD>
                  {!ehSegmentoClinica()&&<TD w={120}>
                    <span style={{ fontSize:13, fontWeight:700, color:cliente.valorMensal>0?"#EE6A1A":"var(--text-muted)" }}>
                      {cliente.valorMensal>0?fmt(cliente.valorMensal):"—"}
                    </span>
                  </TD>}
                  <TD w={110}>
                    <span style={{ fontSize:12, color:"var(--text-secondary)" }}>{fmtData(cliente.dataInicio)}</span>
                  </TD>
                  <TD w={120}>
                    <span style={{ fontSize:12, color:"var(--text-secondary)" }}>{cliente.nomeResponsavel||"—"}</span>
                  </TD>
                  <TD w={44} center>
                    <button onClick={()=>setModal(cliente)} title={t("Editar cliente")} style={{ background:"none", border:"1px solid var(--border-color)", color:"#579bfc", cursor:"pointer", fontSize:12, fontWeight:600, borderRadius:6, padding:"4px 10px", marginRight:6 }}>Editar</button>
                    {ehSegmentoClinica()&&(
                      <button onClick={()=>{ setAbaInicial("prontuario"); setDetalhe(cliente); }} title="Abrir prontuário" style={{ background:"#FDE7DA", border:"1px solid #EE6A1A", borderRadius:7, padding:"3px 9px", color:"#EE6A1A", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", marginRight:6 }}>🩺 Prontuário</button>
                    )}
                    <button onClick={()=>setDetalhe(cliente)} title="Ver detalhes" style={{ background:"none", border:"none", color:"var(--text-muted)", cursor:"pointer", fontSize:14, borderRadius:4, width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center" }}
                      onMouseEnter={e=>{ e.currentTarget.style.background="var(--border-light)"; e.currentTarget.style.color="var(--text-primary)"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color="var(--text-muted)"; }}>→</button>
                  </TD>
                </tr>
              );
            })}

            {!loading&&filtered.length===0&&(
              <tr><td colSpan={ehSegmentoClinica()?9:10} style={{ padding:60, textAlign:"center" }}>
                <div style={{ fontSize:32, marginBottom:12 }}>{ehSegmentoClinica()?"👤":"🏢"}</div>
                <div style={{ fontSize:15, fontWeight:600, color:"var(--text-primary)", marginBottom:6 }}>{t("Nenhum cliente encontrado")}</div>
                <div style={{ fontSize:13, color:"var(--text-muted)" }}>{t('Clique em "+ Novo cliente" para começar')}</div>
              </td></tr>
            )}
          </tbody>
        </table>

        {/* Botão adicionar */}
        <div style={{ padding:"8px 24px" }}>
          <button onClick={()=>setNovaLinha(true)} style={{ background:"none", border:"1.5px dashed #d0d2d9", borderRadius:8, padding:"10px 20px", color:"var(--text-secondary)", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:8, fontFamily:"'Inter',sans-serif", width:"100%" }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor="#EE6A1A"; e.currentTarget.style.color="#EE6A1A"; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor="#d0d2d9"; e.currentTarget.style.color="var(--text-secondary)"; }}>
            {t("+ Adicionar cliente")}
          </button>
        </div>
      </div>

      {modal!==null&&<ModalCliente cliente={Object.keys(modal).length?modal:null} onClose={()=>setModal(null)} onSave={salvar} onDelete={excluir} usuarios={usuarios}/>}
      {detalhe&&<PainelDetalhe abaInicial={abaInicial} cliente={detalhe} onClose={()=>setDetalhe(null)} onAtualizar={()=>{ carregar(); }} usuarios={usuarios}/>}
    </div>
  );
}
